import * as React from "react";
import styled from "styled-components";
import { injectIntl, useIntl, WrappedComponentProps } from "react-intl";
import { connect } from "react-redux";
import { Routine } from "redux-saga-routines";
import {Action, ActionFunctionAny} from "redux-actions";

import { Device } from "../types";
import { formatDate } from "../../utils";
import { saveImage, removeImage, updateTraining, setUserTag, connectDevice } from "../Store/routines";
import confirmModal, { ModalFooter } from "@Safemate/Modals/confirmModal";
import { Checkbox } from "../../../Elements/CheckboxFormik";
import Trained from "../../../Elements/Icon/Icons/Trained";
import Note from "./note";
import { supportGsmStrength } from "../tabDefinition";
import {DeviceHardwareEnum, isGx8, isSTCB} from "../../DeviceHardwareEnum";
import Message from "./message";
import { AppState } from "@Safemate/Store/types";
import { WrapDiv } from "../Sensor/styles";
import { convertScale } from "@Safemate/DeviceList/DeviceList/TableElements";
import SignalBars from "@Safemate/DeviceList/DeviceList/signalBars";
import Dropdown from "@Elements/Dropdown/Dropdown";
import { Option} from "@Elements/Dropdown/types";
import { UserTags } from "@Safemate/enums/UserTags";
import CopyLight from "@Elements/Icon/Icons/CopyLight";
import * as S from "./styles";


const FileInput = styled.input`
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
`;

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  margin: 0;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  background: transparent;
  border: 1px solid ${props => props.theme.colors.buttonBorder};
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
    color: ${props => props.theme.colors.accent};
  }
`;

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  background: transparent;
  border: 1px solid ${props => props.theme.colors.buttonBorder};
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
    color: ${props => props.theme.colors.accent};
  }
`;

export const ImageWrapper = styled.div`
  display: flex;
  background: ${props => props.theme.colors.backgroundSecondary};
  max-width: 100%;
  height: 270px;
  white-space: nowrap;
  text-align: center; 
  margin: 0;
  img {
    margin: auto;
    max-width: 100%;
    max-height: 270px;
    vertical-align: middle;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CopyButton = styled.button<{textBefore: string, textAfter: string}>`
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;

  &:hover,
  &:focus-visible {
    background: rgba(128, 128, 128, 0.18);
    outline: none;
  }

  &::after {
    content: '${props => props.textBefore}';
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translate(-50%, 4px);
    opacity: 0;
    padding: 6px 8px;
    font-size: 12px;
    line-height: 1;
    color: #fff;
    background: rgba(0, 0, 0, 0.85);
    border-radius: 4px;
    pointer-events: none;
    white-space: nowrap;
    transition: opacity 0.15s ease, transform 0.15s ease;
  }

  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translate(-50%, 0);
    border: 6px solid transparent;
    border-top: 0;
    border-bottom-color: rgba(0, 0, 0, 0.85);
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover::after,
  &:focus-visible::after,
  &:hover::before,
  &:focus-visible::before {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  &:active, &:focus {
    outline: none;
    transform: scale(0.98);
    
    &:after {
      content: '${props => props.textAfter}';
    }
  }
}

  @media (prefers-reduced-motion: reduce) {
    &, &::before, &::after { transition: none; }
  }
`;

export const FallbackImage = '/proffweb/images/person-info.svg';

interface InfoProps extends WrappedComponentProps{
  device: Device;
  deviId: number;
  imageUUID: string;
  isBeta: boolean;
  isPrivate: boolean;
  saveImage: Routine<ActionFunctionAny<Action<any>>>;
  updateTraining: Routine<ActionFunctionAny<Action<any>>>;
  setUserTag: Routine<ActionFunctionAny<Action<any>>>;
  connectDevice: Routine<ActionFunctionAny<Action<any>>>;
}

const mapStateToProps = ({ appSettings: {beta}, appData: {isPrivate}, settings: {device: { device }, initialization: {deviId}, info: { imageUUID }}}: AppState) => {
  return{
    device,
    deviId,
    imageUUID,
    isBeta: beta,
    isPrivate
  }
}

const mapDispatchToProps = {
  saveImage,
  updateTraining,
  setUserTag,
  connectDevice
}

const Info = connect(mapStateToProps, mapDispatchToProps)(
  injectIntl(({ isBeta, isPrivate, device, deviId, intl: { formatMessage }, saveImage, updateTraining, setUserTag, connectDevice, imageUUID }: InfoProps) => {

    const isTwin = device.twinChild;
    
    const fallbackImg = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const src = event.currentTarget.src;
      if (src != FallbackImage)
        event.currentTarget.src = FallbackImage;
    }

    const gsmStrength = supportGsmStrength.get(device.dehaId);
    let strength = 0

    if(gsmStrength) {

      const offset = gsmStrength.low === 0 ? 0 : -(gsmStrength.low);
      const scale =  100 / (gsmStrength.high + offset);
      strength = (device.gsmStrength + offset) * scale;

    }

    const removeStatus = isGx8(device.dehaId);
    let displayMac = isGx8(device.dehaId) ;

    const trained = device.trainingApprovedBy !== 0;

    const userTags = [
      {text: formatMessage({id: UserTags.GPS, defaultMessage: "GPS"}), value: UserTags.GPS},
      {text: formatMessage({id: UserTags.MOBILE_ALARM_DEVICE, defaultMessage: "Mobil trygghetsalarm"}), value: UserTags.MOBILE_ALARM_DEVICE},
      {text: formatMessage({id: UserTags.COLLEAGUE_ALERT, defaultMessage: "Kollegavarsling"}), value: UserTags.COLLEAGUE_ALERT},
      {text: formatMessage({id: UserTags.OTHER, defaultMessage: "Annet"}), value: UserTags.OTHER},
      {text: formatMessage({id: UserTags.NONE, defaultMessage: "Ingen"}), value: UserTags.NONE},
    ]

    const copyText = formatMessage({id: "copyToClipboard", defaultMessage: "Kopier til utklippstavle"});
    const copyDoneText = formatMessage({id: "copied", defaultMessage: "Kopiert"})

    const displayConnect = !isPrivate && !device.connected && (device.eview || device.eview07B || device.peaktech || device.takit || device.toplovo);
    const [disableConnect, setDisableConnect] = React.useState(false);

    const connect = () => {
      connectDevice(deviId);
      setDisableConnect(true);
      setTimeout(() => {
        setDisableConnect(false);
      }, 60000);
    }

    const safeDate = (raw: any, showTime = false) => {
      if (!raw) return null;
      const formatted = formatDate(raw, showTime, false, false, false, false, formatMessage);
      if (!formatted || String(formatted).toLowerCase().indexOf("invalid") !== -1) return null;
      return formatted;
    };

    const lastSelfCheck = safeDate(device.lastMsgIn, true);
    const activeSince = safeDate(device.activatedDate);
    const firstActivation = safeDate(device.firstRegistrationDate);

    const showMessages =
      (device.dehaId === DeviceHardwareEnum.WATCH_TWO ||
        device.dehaId === DeviceHardwareEnum.WATCH_FIVE ||
        device.dehaId === DeviceHardwareEnum.WATCH_SEVEN ||
        device.dehaId === DeviceHardwareEnum.WATCH_TWO_4G) &&
      isBeta;

    return (
      <S.InfoPage>
        <S.InfoLayout>
          {/* Media */}
          <S.Media>
            <S.MediaFrame>
              {device.hasUserImage ? (
                <ClickToRevealImage
                  deviId={deviId}
                  alt="User image"
                  onError={fallbackImg}
                  src={`/ajax/file/userimage/${deviId}/?${imageUUID}`}
                  blur={18}
                />
              ) : (
                <S.FallbackImg src={FallbackImage} alt={"User image"} onError={fallbackImg} draggable={false} />
              )}
            </S.MediaFrame>
            {!isTwin && (
              <S.MediaActions>
                {!device.hasUserImage ? (
                  <>
                    <FileInput
                      id="file"
                      name="file"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={(e) => {
                        if (e.target.files)
                          saveImage({
                            deviceId: deviId,
                            file: e.target.files[0]
                          });
                      }}
                    />
                    <Label htmlFor="file">
                      {formatMessage({ id: "addPicture", defaultMessage: "Legg til bilde" })}
                    </Label>
                  </>
                ) : (
                  <EditPicture />
                )}
              </S.MediaActions>
            )}
          </S.Media>

          <S.Content>
            <S.ContentGrid>
              {/* Specifications */}
              <S.Section>
            <S.SectionTitle>
              {formatMessage({ id: "infoHeaderOne", defaultMessage: "Spesifikasjoner" })}
            </S.SectionTitle>
            <S.Dl>
              <S.Row>
                <S.Term>{formatMessage({ id: "connectionInfo", defaultMessage: "Dataforbindelse" })}</S.Term>
                <S.Value>
                  <S.StatusChip>
                    <S.StatusDot $connected={device.connected} />
                    {formatMessage({
                      id: device.connected ? "connected" : "disconnected",
                      defaultMessage: device.connected ? "Tilkoblet" : "Frakoblet"
                    })}
                  </S.StatusChip>
                  {displayConnect && (
                    <S.PrimaryButton onClick={connect} disabled={disableConnect}>
                      {formatMessage({ id: "connect", defaultMessage: "Koble til" })}
                    </S.PrimaryButton>
                  )}
                </S.Value>
              </S.Row>
              {gsmStrength && (
                <S.Row>
                  <S.Term>{formatMessage({ id: "statusBarGsmSignal", defaultMessage: "GSM signal" })}</S.Term>
                  <S.Value>
                    <WrapDiv title={`${convertScale(strength)}/5`}>
                      <SignalBars dehaId={device.dehaId} size={30} strength={device.gsmStrength} />
                    </WrapDiv>
                  </S.Value>
                </S.Row>
              )}
              <S.Row>
                <S.Term>{formatMessage({ id: "gsmNumber", defaultMessage: "GSM-nummer" })}</S.Term>
                <S.Value>
                  {device.formattedPhoneNumE164 || <S.Muted>—</S.Muted>}
                  {device.formattedPhoneNumE164 && (
                    <CopyButton
                      textBefore={copyText}
                      textAfter={copyDoneText}
                      onClick={() => {
                        navigator.clipboard.writeText(device.formattedPhoneNumE164);
                      }}
                    >
                      <CopyLight size="medium" />
                    </CopyButton>
                  )}
                </S.Value>
              </S.Row>
              {device.deviceImei && (
                <S.Row>
                  <S.Term>{formatMessage({ id: "imei", defaultMessage: "IMEI" })}</S.Term>
                  <S.Value>
                    {device.deviceImei}
                    <CopyButton
                      textBefore={copyText}
                      textAfter={copyDoneText}
                      onClick={() => {
                        navigator.clipboard.writeText(device.deviceImei);
                      }}
                    >
                      <CopyLight size="medium" />
                    </CopyButton>
                  </S.Value>
                </S.Row>
              )}
              {displayMac && !isSTCB(device.dehaId) && (
                <S.Row>
                  <S.Term>{formatMessage({ id: "DEVICE_MAC", defaultMessage: "Mac:" })}</S.Term>
                  <S.Value>
                    {device.deviceMac}
                    <CopyButton
                      textBefore={copyText}
                      textAfter={copyDoneText}
                      onClick={() => {
                        navigator.clipboard.writeText(device.deviceMac);
                      }}
                    >
                      <CopyLight size="medium" />
                    </CopyButton>
                  </S.Value>
                </S.Row>
              )}
              <S.Row>
                <S.Term>{formatMessage({ id: "firmwareVersion", defaultMessage: "Firmware-versjon" })}</S.Term>
                <S.Value>{device.fiwaNum || formatMessage({ id: "unknown", defaultMessage: "Ukjent" })}</S.Value>
              </S.Row>
              {isGx8(device.dehaId) && (
                <S.Row>
                  <S.Term>{formatMessage({ id: "status", defaultMessage: "Status" })}</S.Term>
                  <S.Value>
                    <DeviceStatus />
                  </S.Value>
                </S.Row>
              )}
            </S.Dl>
          </S.Section>

          {/* Information */}
          <S.Section>
            <S.SectionTitle>
              {formatMessage({ id: "infoHeaderTwo", defaultMessage: "Informasjon" })}
            </S.SectionTitle>
            <S.Dl>
              {!isPrivate && (
                <S.StackRow>
                  <Checkbox
                    readOnly={isTwin}
                    disable={isTwin}
                    outerDiv={{ float: "none" }}
                    value={trained}
                    name={"trained"}
                    id={"trained"}
                    label={formatMessage({ id: "userTraining", defaultMessage: "Bruker har fått opplæring" })}
                    labelComp={trained && <Trained size="medium" inline />}
                    labelStyle={{ display: "flex", alignItems: "center" }}
                    onChange={(event: React.SyntheticEvent<HTMLInputElement>) => {
                      updateTraining({
                        deviceId: deviId,
                        status: event.currentTarget.checked
                      });
                    }}
                  />
                  {trained && (
                    <S.TrainedNote>
                      {`${formatMessage({
                        id: "trainingApprovedBy",
                        defaultMessage: "Opplæring godkjent av"
                      })} ${device.trainingApprovedByName}`}
                    </S.TrainedNote>
                  )}
                </S.StackRow>
              )}
              <S.Row>
                <S.Term>{formatMessage({ id: "lastSelfCheckInfo", defaultMessage: "Siste selvsjekk" })}</S.Term>
                <S.Value>{lastSelfCheck || formatMessage({ id: "never", defaultMessage: "Aldri" })}</S.Value>
              </S.Row>
              <S.Row>
                <S.Term>{formatMessage({ id: "activeSinceInfo", defaultMessage: "Aktiv siden" })}</S.Term>
                <S.Value>{activeSince || <S.Muted>—</S.Muted>}</S.Value>
              </S.Row>
              <S.Row>
                <S.Term>{formatMessage({ id: "firstActivationInfo", defaultMessage: "Først aktivert" })}</S.Term>
                <S.Value>{firstActivation || <S.Muted>—</S.Muted>}</S.Value>
              </S.Row>
              {device.watchRemovedEnabled && (
                <S.Row>
                  <S.Term>
                    {formatMessage({ id: "watchRemovedInfoBoxText", defaultMessage: "Watch is worn by user" })}
                  </S.Term>
                  <S.Value>
                    {device.watchRemoved
                      ? formatMessage({ id: "no", defaultMessage: "NO" })
                      : formatMessage({ id: "yes", defaultMessage: "Yes" })}
                  </S.Value>
                </S.Row>
              )}
              {!device.climax && (
                <S.Row>
                  <S.Term>{formatMessage({ id: "userTags", defaultMessage: "Brukerkategori" })}</S.Term>
                  <S.Value>
                    <Dropdown
                      options={userTags}
                      onChange={(opt) => {
                        const value: UserTags = (opt as Option).value;
                        setUserTag({ deviceId: deviId, userTag: value });
                      }}
                      initial={device.userTag || UserTags.NONE}
                    />
                  </S.Value>
                </S.Row>
              )}
            </S.Dl>
          </S.Section>
            </S.ContentGrid>

            {/* Messages */}
            {showMessages && (
              <S.MessagesBlock>
                <S.SectionTitle>
                  {formatMessage({ id: "messages", defaultMessage: "Meldinger" })}
                </S.SectionTitle>
                <S.MessagesInner>
                  <Message />
                </S.MessagesInner>
              </S.MessagesBlock>
            )}
          </S.Content>
        </S.InfoLayout>
      </S.InfoPage>
    )
  })
)

export default Info;

interface RemovePictureProps{
  removeImage: Routine<ActionFunctionAny<Action<any>>>;
  saveImage: Routine<ActionFunctionAny<Action<any>>>;
  deviId: number;
}

const mapEditPictureStateToProps = ({ settings: {initialization: {deviId}}}: AppState) => {
  return{
    deviId
  }
}

const mapEditPictureDispatchToProps = {
  removeImage,
  saveImage
}

const EditPicture = connect(mapEditPictureStateToProps, mapEditPictureDispatchToProps)(({removeImage, saveImage, deviId}: RemovePictureProps) => {

    const { Comp, func } = confirmModal();

    const { formatMessage } = useIntl();
    return(
      <React.Fragment>
        <StyledButton onClick={() => func(true)}>
          {formatMessage({id: "editPicture", defaultMessage: "Rediger bilde"})}
        </StyledButton>
        <Comp
          title={`${formatMessage({id: "edit", defaultMessage: "Rediger"})} ${formatMessage({id: "userPicture", defaultMessage: "brukerbilde."})}`}
          body={
            <p>
              <span>{formatMessage({id: "editPictureDesc", defaultMessage: "Endre eller slett bruker bilde, handlingen kan ikke angres."})}</span>
            </p>
          }
          customFooter={
            <ModalFooter>
                <ButtonWrapper>
                  <>
                    <FileInput
                      id="file"
                      name="file"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={(e) => {
                        if(e.target.files)
                        saveImage({
                          deviceId: deviId,
                          file: e.target.files[0]
                        })
            func(false);
          }}
        />
                    <Label htmlFor="file">
                      {formatMessage({id: "addPicture", defaultMessage: "Legg til bilde"})}
                    </Label>
                  </>
                  <StyledButton onClick={() => {
                    removeImage(deviId);
                    func(false);
                  }}>
                    {formatMessage({id: "removePicture", defaultMessage: "Fjern bilde"})}
                  </StyledButton>  
                </ButtonWrapper>
            </ModalFooter>
          }
        />
      </React.Fragment>
    )
})

interface DeviceStatusProps extends WrappedComponentProps{
  device: Device;
}

const mapDeviceStaticStateToProps = ({ settings: {device: {device }}}: AppState) => {
  return{
    device
  }
}

export const DeviceStatus = connect(mapDeviceStaticStateToProps)(
  injectIntl(({intl: { formatMessage: fm }, device}: DeviceStatusProps) => {

    let status: string = fm({id: "ok", defaultMessage: "Ok"});

    if(device.neverUsed) status = fm({id: "new", defaultMessage: "Ny"});
    else if(device.inAlarmMode) status = fm({id: "alarm", defaultMessage: "Alarm"});
    else if(device.offline) status = fm({id: "offline", defaultMessage: "Avskrudd"});
    else if(device.failedSelfCheck) status = fm({id: "selfCheckStatusHover", defaultMessage: "Feilet selvsjekk"});
    else if(device.batteryLow) status = fm({id: "lowBattery", defaultMessage: "lavt batteri"});
    else if(device.staticTooLong) status = fm({id: "static", defaultMessage: "Ikke i aktiv bruk"});
    else if(device.deviceStatus){
      status = fm({id: device.deviceStatus, defaultMessage: device.deviceStatus});
    }
    else if(!device.connected){
      status = fm({id: "panelOffline", defaultMessage: "Frakoblet"});
    }

    return(
      <span>{`${status.charAt(0).toUpperCase()}${status.slice(1)}`}</span>
    )  
  })
)


const Wrapper = styled.button<{ $revealed: boolean; $blur: number }>`
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 10px;
  padding: 0;
  border: 0;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #7aa2ff;
    outline-offset: 2px;
  }

  img {
    inset: 0;
    object-fit: cover;
    transition: filter 220ms ease, transform 220ms ease, opacity 220ms ease;
    filter: ${({ $revealed, $blur }) =>
      $revealed ? "none" : `blur(${$blur}px)`};
    transform: ${({ $revealed }) => ($revealed ? "none" : "scale(1.02)")};
  }
`;

const Overlay = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #111;
  font: 600 13px/1 system-ui, -apple-system, Segoe UI, Roboto, Helvetica,
    Arial, sans-serif;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
  user-select: none;
  pointer-events: none;
  letter-spacing: 0.2px;
`;

type ClickToRevealImageProps = {
  src: string;
  deviId: number;
  alt?: string;
  initiallyHidden?: boolean;
  blur?: number;
  onError?: React.ReactEventHandler<HTMLImageElement>;
  className?: string;
};

export const ClickToRevealImage = ({
  src,
  alt = "",
  initiallyHidden = true,
  blur = 16,
  onError,
  className,
}: ClickToRevealImageProps) => {
  const [revealed, setRevealed] = React.useState(!initiallyHidden);

  const { formatMessage } = useIntl();
  
  const reveal = () => {
    if(!revealed){
      setRevealed(true);
    }
  };

  return (
    <Wrapper
      type="button"
      aria-pressed={revealed}
      aria-label={revealed ? "Image shown" : "Click to display image"}
      onClick={reveal}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          reveal();
        }
      }}
      $revealed={revealed}
      $blur={blur}
      className={className}
    >
      <img src={!revealed ? "" : src} alt={alt} onError={onError} draggable={false} />
      {!revealed && <Overlay>{formatMessage({id: "revealImage", defaultMessage: "Click to show"})}</Overlay>}
    </Wrapper>
  );
};
